import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../PlaceholderTag";

/** Source geometry follows the pen's existing fixed-step gate/progress owner. */
export class KittenShelterVisuals {
  readonly ready: Promise<void>;
  private readonly root: TransformNode;
  private disposed = false;
  private model: InstantiatedModel | null = null;
  private gate: AbstractMesh | null = null;
  private gateBaseY = 0;
  private lift = 0;
  private count = 0;
  private markerMaterials: PBRMaterial[] = [];
  private casters: AbstractMesh[] = [];

  constructor(private readonly scene: Scene, floorPosition: Vector3,
    onLoaded: () => void, private readonly shadows?: ShadowGenerator) {
    this.root = new TransformNode("kittenShelter", scene);
    this.root.position.copyFrom(floorPosition);
    this.ready = this.load(onLoaded);
  }

  private async load(onLoaded: () => void): Promise<void> {
    try {
      const model = await getAssetManager().instantiateModel(this.scene,
        "/assets/models/environment/kitten_shelter.glb", undefined, { doNotInstantiate: true });
      if (this.disposed || this.scene.isDisposed) { model.dispose(); return; }
      const gate = model.meshes.find((m) => m.name.endsWith("ShelterGate_slide"));
      const markers = [0, 1, 2].map((i) => model.meshes.find((m) => m.name.endsWith("ShelterMarker_" + i)));
      if (!gate || markers.some((m) => !(m?.material instanceof PBRMaterial))) {
        model.dispose(); throw new Error("Kitten shelter is missing its gate or status lenses");
      }
      for (const root of model.rootNodes) { root.parent = this.root; tagAs(root, "final"); }
      for (const mesh of model.meshes) {
        mesh.isPickable = false; mesh.receiveShadows = true; tagAs(mesh, "final");
        if (mesh.getTotalVertices() > 0 && !markers.includes(mesh)) {
          this.shadows?.addShadowCaster(mesh, false); this.casters.push(mesh);
        }
      }
      this.model = model; this.gate = gate; this.gateBaseY = gate.position.y;
      const replaced = new Set(markers.map((mesh) => mesh!.material!));
      this.markerMaterials = markers.map((mesh, i) => {
        const material = (mesh!.material as PBRMaterial).clone("shelterStatus_" + i);
        mesh!.material = material; material.metallic = .15; material.roughness = .38;
        return material;
      });
      for (const material of replaced) {
        if (!model.meshes.some((mesh) => mesh.material === material)) material.dispose();
      }
      this.applyState(); onLoaded();
    } catch (error) {
      if (!this.disposed) console.warn("[KittenShelterVisuals] Keeping pen fallback:", error);
    }
  }

  setState(lift: number, count: number): void {
    this.lift = Math.max(0, Math.min(1, lift));
    this.count = Math.max(0, Math.min(3, count)); this.applyState();
  }

  private applyState(): void {
    if (this.gate) this.gate.position.y = this.gateBaseY + this.lift * .72;
    this.markerMaterials.forEach((material, i) => {
      const safe = i < this.count;
      material.albedoColor = safe ? new Color3(.12, .32, .17) : new Color3(.25, .18, .09);
      material.emissiveColor = safe ? new Color3(.015, .075, .025) : Color3.Black();
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const mesh of this.casters) this.shadows?.removeShadowCaster(mesh, false);
    this.casters = [];
    // Assigned marker materials are owned and disposed by the model's nodes.
    this.markerMaterials = []; this.gate = null;
    this.model?.dispose(); this.model = null; this.root.dispose();
  }
}
